import type { MapBlockProps, Listing } from '../types';
import { theme, sectionColors } from '../theme';

/**
 * Property map. SERVER-SAFE: renders a Mapbox **Static Images** API <img> (no
 * "use client", no mapbox-gl dependency — which keeps the shared renderer usable
 * from both the Next tenant site and the Vite builder). Markers come from the
 * page's listings (host-provided coords); the access token is host-provided via
 * `data.mapboxToken`. With no token it shows a graceful placeholder; with a token
 * but no listing coords it shows the default region.
 */

const MARKER_COLOR = '4E1F60'; // deep plum — the default when no markerColor is set

/** The Mapbox static pin colour: a hex without '#'. Anything else (a name, a CSS function) → the default. */
export function markerHex(value: string | undefined): string {
  const m = (value ?? '').trim().match(/^#?([0-9a-f]{6}|[0-9a-f]{3})$/i);
  return m ? m[1].toUpperCase() : MARKER_COLOR;
}
const DEFAULT_POSITION = '-80.4,27.2,5.5'; // south-Florida fallback: lng,lat,zoom
const MAX_MARKERS = 15;

export function MapBlock(
  props: MapBlockProps & { listings?: Listing[]; mapboxToken?: string | null },
) {
  const { heading, height = 480, listings = [], mapboxToken } = props;
  const colors = sectionColors(props, { bg: theme.surface, text: theme.text });

  const points = listings
    .map((l) => ({ lat: l.address?.latitude, lng: l.address?.longitude }))
    .filter(
      (p): p is { lat: number; lng: number } =>
        typeof p.lat === 'number' && Number.isFinite(p.lat) &&
        typeof p.lng === 'number' && Number.isFinite(p.lng),
    )
    .slice(0, MAX_MARKERS);

  let content;
  if (!mapboxToken) {
    content = (
      <div
        className="flex items-center justify-center rounded-md border border-black/10 bg-black/[0.04] text-sm text-black/50"
        style={{ height }}
      >
        Map appears here once a Mapbox token is connected.
      </div>
    );
  } else {
    // Static API path: `{markers}/auto` to fit pins, or `{lng,lat,zoom}` with none.
    const overlay = points.length
      ? `${points.map((p) => `pin-s+${markerHex(props.markerColor)}(${p.lng.toFixed(5)},${p.lat.toFixed(5)})`).join(',')}/auto`
      : DEFAULT_POSITION;
    const src = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${overlay}/1280x480@2x?access_token=${encodeURIComponent(mapboxToken)}`;
    content = (
      <img
        src={src}
        alt={heading || 'Property map'}
        width={1280}
        height={480}
        loading="lazy"
        decoding="async"
        className="w-full rounded-md border border-black/10 object-cover"
        style={{ height }}
      />
    );
  }

  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      <div className="mx-auto max-w-5xl">
        {heading ? (
          <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>{heading}</h2>
        ) : null}
        {content}
      </div>
    </section>
  );
}
